import React, { useState, useEffect } from 'react';
import { advisorsAPI, rdvAdminAPI } from '../services/api';
import { getSocket } from '../services/socket';

// ─────────────────────────────────────────────────────────────────────────────
// Advisors — the insurance advisors that agents book consultations with.
// The team changes regularly, so names and calendar/spreadsheet links are
// edited here and picked up live by the agent calling script (RDV step).
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY = { name: '', calendar_url: '', spreadsheet_url, calendar_top_clip: 300, calendar_bottom_clip: 200, access_password: '', active: 1, sort_order: 0 };

export default function Advisors() {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | {} (new) | advisor row
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null); // { advisor, url } — modal with the RDV page link

  const load = async () => {
    try {
      const list = await advisorsAPI.listAll();
      setAdvisors(list);
      return list;
    } catch (e) {
      setError(e.error || 'Failed to load clients');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Karim 2026-10-20 : un RDV généré par le script (ou un client édité)
    // rafraîchit le CRM en direct — le ping advisors:refresh arrive déjà de
    // advisors.js ; /rdv/book l'émet désormais aussi.
    const s = getSocket();
    const onRefresh = () => load();
    if (s) s.on('advisors:refresh', onRefresh);
    return () => { if (s) s.off('advisors:refresh', onRefresh); };
  }, []);

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editing.id) {
        // Edit can now also generate a password (backend PUT auto-generates
        // when the client has none). Open the link modal — same as create —
        // so link + password can be copied together (restored 2026-10-15:
        // the edit flow silently swallowed the generated password).
        const r = await advisorsAPI.update(editing.id, editing);
        setEditing(null);
        const list = await load();
        if (r?.generated) {
          const fresh = list.find(a => a.id === editing.id);
          if (fresh) getLink(fresh);
        }
      } else {
        // Password auto-generated when empty — open the link modal right away
        // so the admin can copy link + password together.
        const r = await advisorsAPI.create(editing);
        setEditing(null);
        const list = await load();
        const fresh = list.find(a => a.id === r.id);
        if (fresh) getLink(fresh);
      }
    } catch (e) {
      setError(e.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await advisorsAPI.remove(id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e.error || 'Delete failed');
    }
  };

  // Generate/fetch the advisor's private Rendez-vous page link.
  const getLink = async (adv, regenerate = false) => {
    try {
      const r = await rdvAdminAPI.advisorLink(adv.id, regenerate);
      setLinkInfo({ advisor: adv, url: `${window.location.origin}${r.url_path}`, regenerate });
    } catch (e) {
      setError(e.error || 'Link generation failed');
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
  };

  const field = (label, key, placeholder, type = 'text') => (
    <div key={key} style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</label>
      <input
        className="input"
        type={type}
        value={editing[key] ?? ''}
        placeholder={placeholder}
        onChange={e => setEditing({ ...editing, [key]: e.target.value })}
        style={{ width: '100%', padding: '9px 12px', fontSize: 13 }}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>Clients</h1>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>
            Clients du script d'appel — noms + liens calendrier éditables ici, utilisés en direct par les agents.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })}>+ Add Client</button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Name</th>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Calendar</th>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Spreadsheet</th>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Order</th>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Status</th>
                <th style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {advisors.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>No clients yet — add the first one.</td></tr>
              )}
              {advisors.map(adv => (
                <tr key={adv.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{adv.name}</td>
                  <td style={{ padding: '10px 14px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adv.calendar_url
                      ? <a href={adv.calendar_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>📅 Link</a>
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adv.spreadsheet_url
                      ? <a href={adv.spreadsheet_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>📊 Link</a>
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }}>{adv.sort_order}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                      background: adv.active ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.15)',
                      color: adv.active ? '#059669' : '#64748b',
                    }}>
                      {adv.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => getLink(adv)} title="Lien page Rendez-vous du client">🔗 Lien RDV</button>
                    <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => setEditing({ ...adv, access_password: '' })}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(adv)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / create modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius)', padding: 24, width: 460, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 18px', fontSize: 17, fontWeight: 800 }}>
              {editing.id ? 'Edit Client' : 'Add Client'}
            </h2>
            {field('Name', 'name', 'ex : Jean Tremblay')}
            {field('Calendar URL (Google Calendar…)', 'calendar_url', 'https://calendar.google.com/…')}
            {field('Spreadsheet URL (Suivi…)', 'spreadsheet_url', 'https://docs.google.com/spreadsheets/…')}
            {field('Décalage haut (px)', 'calendar_top_clip', '300 = masque le nom du conseiller', 'number')}
            {field('Décalage bas (px)', 'calendar_bottom_clip', '200 = masque téléphone/pied de page', 'number')}
            {field('Sort order', 'sort_order', '0', 'number')}
            {field(
              editing.id ? 'RDV page password (laisser vide = inchangé)' : 'RDV page password (vide = généré automatiquement)',
              'access_password',
              'mot de passe client pour sa page Rendez-vous'
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked ? 1 : 0 })} />
                Active (visible dans le script d'appel des agents)
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !String(editing.name || '').trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius)', padding: 24, width: 380, boxShadow: 'var(--shadow-lg)' }}>
            <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800 }}>Delete client ?</h2>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 18 }}>
              « {confirmDelete.name} » sera retiré du script d'appel des agents. Cette action est définitive.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => remove(confirmDelete.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* RDV page link modal */}
      {linkInfo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setLinkInfo(null); }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius)', padding: 24, width: 520, boxShadow: 'var(--shadow-lg)' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 800 }}>🔗 Page Rendez-vous — {linkInfo.advisor.name}</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Envoyez ce lien privé au client. Il entre son mot de passe une fois, voit ses rendez-vous
              en direct et peut y inscrire ses résultats (Rencontre / No show / Injoignable / 2ᵉ RDV).
              {!linkInfo.advisor.access_password && !linkInfo.regenerate && ' ⚠️ Définissez d\'abord un mot de passe (Edit).'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input readOnly value={linkInfo.url}
                style={{ flex: 1, padding: '9px 12px', fontSize: 12, fontFamily: 'var(--mono)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-secondary)' }}
                onFocus={e => e.target.select()} />
              <button type="button" className="btn btn-primary" onClick={() => copyLink(linkInfo.url)}>Copier</button>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>Mot de passe client</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input readOnly value={linkInfo.advisor.access_password || '⚠️ Aucun mot de passe défini (Edit pour l\'ajouter)'}
                style={{ flex: 1, padding: '9px 12px', fontSize: 12, fontFamily: 'var(--mono)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-secondary)' }}
                onFocus={e => e.target.select()} />
              {linkInfo.advisor.access_password && (
                <button type="button" className="btn btn-primary" onClick={() => copyLink(linkInfo.advisor.access_password)}>Copier</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => getLink(linkInfo.advisor, true)}>↻ Nouveau lien (invalide l'ancien)</button>
              <button className="btn btn-secondary" onClick={() => setLinkInfo(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
